// public/js/modules/pushNotifications.js
// Client-side Firebase Cloud Messaging (FCM) setup for Octave11

import { getMessaging, getToken, deleteToken, onMessage } from 'https://www.gstatic.com/firebasejs/9.23.0/firebase-messaging.js';
import { getFirestore, doc, setDoc, deleteDoc, serverTimestamp, getDoc, onSnapshot } from 'https://www.gstatic.com/firebasejs/9.23.0/firebase-firestore.js';
import { getAuth } from 'https://www.gstatic.com/firebasejs/9.23.0/firebase-auth.js';
import { getApp } from 'https://www.gstatic.com/firebasejs/9.23.0/firebase-app.js';
import { firebaseConfig, vapidKey } from './firebaseConfig.js';

const STORAGE_KEY = 'octave11_push_enabled';
const DEFAULT_IMAGE = 'https://firebasestorage.googleapis.com/v0/b/f8lyss-music-app-63fb7.firebasestorage.app/o/OCT11LOGO4REAL.png?alt=media&token=3edc951f-9a48-41a8-bf4f-08a202eceabe';

function normalizePushImageUrl(url) {
  if (!url || typeof url !== 'string' || url === DEFAULT_IMAGE) return url;
  if (url.startsWith('https://wsrv.nl/')) return url;
  return `https://wsrv.nl/?url=${encodeURIComponent(url)}&w=1024&h=512&fit=contain&cbg=black&output=png`;
}

let messaging = null;
let unsubscribers = [];

function getStoredPushDecision() {
  try {
    const val = localStorage.getItem(STORAGE_KEY);
    return val ? JSON.parse(val) : null;
  } catch (e) {
    return null;
  }
}

function setStoredPushDecision(enabled) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(enabled));
  } catch (e) {
    // ignore
  }
}

function tokenHash(token) {
  let hash = 0;
  if (!token || token.length === 0) return '' + hash;
  for (let i = 0; i < token.length; i++) {
    const char = token.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash |= 0;
  }
  return Math.abs(hash).toString(16).padStart(8, '0');
}

function ensureMessaging() {
  if (!messaging) {
    messaging = getMessaging(getApp());
  }
  return messaging;
}

async function getActiveServiceWorkerRegistration() {
  if (!('serviceWorker' in navigator)) return null;
  try {
    return await navigator.serviceWorker.ready;
  } catch (e) {
    console.warn('[Push] Could not get active service worker registration:', e);
    return null;
  }
}

export async function getCurrentPushToken() {
  const msg = ensureMessaging();
  if (!msg) return null;
  if (vapidKey === 'REPLACE_WITH_VAPID_PUBLIC_KEY') {
    console.warn('[Push] VAPID key placeholder not replaced; token cannot be generated yet');
    return null;
  }
  const swReg = await getActiveServiceWorkerRegistration();
  if (!swReg) {
    console.warn('[Push] No active service worker registration; cannot get token');
    return null;
  }
  try {
    return await getToken(msg, { vapidKey, serviceWorkerRegistration: swReg });
  } catch (e) {
    console.warn('[Push] getToken failed:', e);
    return null;
  }
}

async function persistTokenForUser(uid, token) {
  if (!uid || !token) return false;
  const db = getFirestore();
  const id = tokenHash(token);
  await setDoc(doc(db, 'users', uid, 'fcmTokens', id), {
    token,
    platform: 'web',
    userAgent: navigator.userAgent.slice(0, 200),
    updatedAt: serverTimestamp(),
    createdAt: serverTimestamp()
  }, { merge: true });
  return true;
}

async function removeTokenForUser(uid, token) {
  if (!uid || !token) return;
  const db = getFirestore();
  const id = tokenHash(token);
  await deleteDoc(doc(db, 'users', uid, 'fcmTokens', id));
}

export async function requestPushPermissionAndToken(uid = null) {
  if (!('Notification' in window)) {
    console.warn('[Push] Notifications API not supported');
    return { enabled: false, reason: 'unsupported' };
  }
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    setStoredPushDecision(false);
    return { enabled: false, reason: permission };
  }

  const token = await getCurrentPushToken();
  if (!token) {
    console.warn('[Push] getCurrentPushToken returned no token');
    setStoredPushDecision(false);
    return { enabled: false, reason: 'token-failed' };
  }

  const targetUid = uid || getAuth().currentUser?.uid;
  if (targetUid) {
    const persisted = await persistTokenForUser(targetUid, token);
    if (persisted) {
      console.log('[Push] Token persisted for user', targetUid, 'hash:', tokenHash(token));
    }
  } else {
    console.warn('[Push] FCM token obtained but no uid available to persist it');
  }
  setStoredPushDecision(true);
  return { enabled: true, token };
}

export async function disablePushNotifications(uid = null) {
  setStoredPushDecision(false);
  if (!messaging) return;
  const swReg = await getActiveServiceWorkerRegistration();
  const token = swReg
    ? await getToken(messaging, { vapidKey, serviceWorkerRegistration: swReg })
    : await getToken(messaging, { vapidKey });
  const targetUid = uid || getAuth().currentUser?.uid;
  if (token && targetUid) {
    await removeTokenForUser(targetUid, token);
  }
  if (token) {
    await deleteToken(messaging);
  }
}

export function isPushPermissionGranted() {
  if (!('Notification' in window)) return false;
  return Notification.permission === 'granted';
}

export function shouldPromptForPush() {
  if (!('Notification' in window)) return false;
  const stored = getStoredPushDecision();
  return Notification.permission === 'default' && stored !== false;
}

function getDefaultNotificationPreferences() {
  return {
    enabled: true,
    foregroundNotifications: true,
    categories: {
      dm: false,
      mentions: false,
      comments: false,
      likes: false,
      coins: false,
      live: true,
      showReminders: true,
      lotteryWins: true,
      currentSong: true,
      likesSent: false,
      commentsSent: false,
      addedSongs: false
    },
    quietHours: { enabled: false, start: '22:00', end: '08:00' }
  };
}

function mergeNotificationPreferences(stored) {
  const defaults = getDefaultNotificationPreferences();
  if (!stored || typeof stored !== 'object') return defaults;
  return {
    ...defaults,
    ...stored,
    categories: { ...defaults.categories, ...(stored.categories || {}) },
    quietHours: { ...defaults.quietHours, ...(stored.quietHours || {}) }
  };
}

let cachedNotificationPreferences = mergeNotificationPreferences();

function isPushEnabled(prefs) {
  if (prefs && typeof prefs.enabled === 'boolean') return prefs.enabled;
  const stored = getStoredPushDecision();
  if (stored === false) return false;
  return true;
}

let lastPushEnabledState = undefined;

async function syncTokenWithEnabledState(enabled, uid = null) {
  if (enabled === lastPushEnabledState) return;
  lastPushEnabledState = enabled;
  if (enabled) {
    if (Notification.permission === 'denied') {
      console.log('[Push] Notifications denied by user; cannot enable token');
      return;
    }
    const result = await requestPushPermissionAndToken(uid);
    if (!result.enabled) {
      console.warn('[Push] Could not obtain token:', result.reason);
    }
  } else {
    if (Notification.permission === 'granted') {
      await disablePushNotifications(uid);
    } else {
      setStoredPushDecision(false);
    }
  }
}

export function initPushNotifications() {
  let msg;
  try {
    msg = ensureMessaging();
  } catch (e) {
    console.warn('[Push] Could not initialize messaging:', e);
    return;
  }

  const auth = getAuth();
  let profileUnsubscribe = null;

  // Sync (and keep synced) the FCM token based on preferences.notifications.enabled.
  unsubscribers.push(
    auth.onAuthStateChanged(async (user) => {
      if (profileUnsubscribe) {
        profileUnsubscribe();
        profileUnsubscribe = null;
      }
      if (!user) return;

      const db = getFirestore();
      const userRef = doc(db, 'users', user.uid);

      const capturedUid = user.uid;
      try {
        const initialSnap = await getDoc(userRef);
        const prefs = initialSnap.data()?.preferences?.notifications;
        cachedNotificationPreferences = mergeNotificationPreferences(prefs);
        await syncTokenWithEnabledState(isPushEnabled(cachedNotificationPreferences), capturedUid);
      } catch (e) {
        console.warn('[Push] Failed to read initial notification preferences:', e);
      }

      profileUnsubscribe = onSnapshot(userRef, async (snap) => {
        const prefs = snap.data()?.preferences?.notifications;
        cachedNotificationPreferences = mergeNotificationPreferences(prefs);
        await syncTokenWithEnabledState(isPushEnabled(cachedNotificationPreferences), capturedUid);
      });
    })
  );

  // Foreground messages - mirror into the existing in-app bell if available
  unsubscribers.push(
    onMessage(msg, async (payload) => {
      console.log('[Push] Foreground message:', payload);
      if (window.notificationsManagerModule?.refreshNotifications) {
        window.notificationsManagerModule.refreshNotifications();
      }

      // Show an OS notification only when the tab is actually visible and the user
      // has foreground notifications enabled. Background/inactive tabs are handled
      // by the service worker, so we must avoid duplicating the OS toast here.
      if (document.visibilityState === 'visible' && !document.hidden) {
        try {
          const user = getAuth().currentUser;
          let foregroundEnabled = true;
          if (user) {
            const snap = await getDoc(doc(getFirestore(), 'users', user.uid));
            const prefs = snap.data()?.preferences?.notifications;
            foregroundEnabled = prefs && typeof prefs.foregroundNotifications === 'boolean'
              ? prefs.foregroundNotifications
              : getDefaultNotificationPreferences().foregroundNotifications;
          }
          if (!foregroundEnabled) return;

          const data = payload?.data || {};
          console.log('[Push] Foreground permission:', Notification.permission);
          console.log('[Push] Foreground enabled:', foregroundEnabled);
          console.log('[Push] Foreground payload data:', data);

          const swReg = await getActiveServiceWorkerRegistration();
          if (!swReg) {
            console.warn('[Push] No active service worker for foreground notification');
            return;
          }

          const title = data.title || 'Octave11';
          const options = {
            body: data.body || 'You have a new notification',
            icon: data.icon || 'https://firebasestorage.googleapis.com/v0/b/f8lyss-music-app-63fb7.firebasestorage.app/o/Favicon.png?alt=media&token=5d9f7e65-001f-4c72-b198-8f86769bf499',
            image: data.image || data.icon || 'https://firebasestorage.googleapis.com/v0/b/f8lyss-music-app-63fb7.firebasestorage.app/o/Favicon.png?alt=media&token=5d9f7e65-001f-4c72-b198-8f86769bf499',
            tag: data.tag || 'general',
            renotify: data.renotify === 'true',
            requireInteraction: data.requireInteraction === 'true',
            data: { clickAction: data.clickAction || 'https://octave11.com/', tag: data.tag || 'general' }
          };

          if (data.actions) {
            try {
              const parsed = JSON.parse(data.actions);
              if (Array.isArray(parsed) && parsed.length) options.actions = parsed;
            } catch (e) {
              // ignore malformed actions
            }
          }

          console.log('[Push] Showing foreground notification via service worker:', title, options);
          await swReg.showNotification(title, options);
        } catch (e) {
          console.warn('[Push] Foreground OS notification failed:', e);
        }
      }
    })
  );

  // Relay service worker diagnostics (background push) into the main console
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.addEventListener('message', (event) => {
      const data = event?.data;
      if (!data || !data.type) return;
      if (data.type === 'push-received') {
        console.log('[Push] SW received background message:', data);
      } else if (data.type === 'push-shown') {
        console.log('[Push] SW showNotification succeeded:', data);
      } else if (data.type === 'push-error') {
        console.error('[Push] SW showNotification failed:', data);
      } else if (data.type === 'notification-click') {
        console.log('[Push] Notification clicked, clickAction:', data.clickAction);
        window.dispatchEvent(new CustomEvent('octave11-notification-click', { detail: data }));
      }
    });
  }

  if (!('Notification' in window)) {
    console.warn('[Push] Notifications API not supported');
    return;
  }

  console.log('[Push] Initial permission state:', Notification.permission);
  window.requestTestPush = requestTestPush;
}

export async function requestTestPush(tag = 'general', title = 'Test', body = 'This is a test push') {
  try {
    const { httpsCallable, getFunctions } = await import('https://www.gstatic.com/firebasejs/9.23.0/firebase-functions.js');
    const functions = getFunctions(getApp());
    const sendTestPush = httpsCallable(functions, 'sendTestPush');
    const result = await sendTestPush({ tag, title, body });
    console.log('[Push] Test push result:', result.data);
    return result.data;
  } catch (e) {
    console.error('[Push] Test push failed:', e);
    throw e;
  }
}

export async function notifyCurrentSong(songData, isLiveSong = false) {
  if (!songData || !songData.id) return;
  if (isLiveSong) return;
  if (!('Notification' in window) || Notification.permission !== 'granted') return;

  const user = getAuth().currentUser;
  if (!user) return;

  try {
    const db = getFirestore();
    const userRef = doc(db, 'users', user.uid);
    const snap = await getDoc(userRef);
    const prefs = snap.data()?.preferences?.notifications;
    if (!isPushEnabled(prefs)) return;
    if (prefs?.categories?.currentSong === false) return;

    const title = 'Now playing';
    const body = `${songData.title || 'A song'} by ${songData.artistName || 'Unknown Artist'}`;
    const icon = 'https://firebasestorage.googleapis.com/v0/b/f8lyss-music-app-63fb7.firebasestorage.app/o/Favicon.png?alt=media&token=5d9f7e65-001f-4c72-b198-8f86769bf499';
    const rawImage = songData.pushImageUrl || songData.discordImageUrl || songData.albumArtUrl || DEFAULT_IMAGE;
    const image = normalizePushImageUrl(rawImage);
    const clickAction = `https://octave11.com/?song=${encodeURIComponent(songData.id)}`;

    const swReg = await getActiveServiceWorkerRegistration();
    if (!swReg) return;

    const options = {
      body,
      tag: 'currentSong',
      icon,
      image,
      renotify: true,
      requireInteraction: false,
      data: { clickAction, tag: 'currentSong' }
    };

    console.log('[Push] Showing local currentSong notification:', title, options);
    await swReg.showNotification(title, options);
  } catch (e) {
    console.warn('[Push] notifyCurrentSong failed:', e);
  }
}

export function getCachedNotificationPreferences() {
  return cachedNotificationPreferences;
}

export function cleanupPushNotifications() {
  unsubscribers.forEach(fn => fn && typeof fn === 'function' ? fn() : null);
  unsubscribers = [];
}
